import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useBatchCreateTags, useContinueTag } from 'app/api/tag-queries/tags';
import { TagType } from 'app/api/tag-queries/types';
import { useAddTagsToCells } from 'app/views/documents/reference-data/reference-data-queries';
import { setReferenceData } from 'app/state/redux/documentSlice';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { TagContext } from 'app/state/contexts/TagContext';

interface Cell {
  row: number;
  col: number;
  value?: string | number | null;
  conceptId?: string;
  linkroleUri?: string | null;
}

interface SectionContext {
  documentSuperId: number;
  sectionSuperId: string;
  pageId: string;
}

interface BaseInput {
  cells: Cell[];
  referenceDataSuperId: string;
  conceptId: string;
  type: TagType;
  taxonomyId: string;
  sectionContext: SectionContext;
  locale: string;
  onTagsCreated?: () => void;
  blockedTagIds?: Set<string>;
  origin?: 'human' | 'ai';
  linkroleUri?: string | null;
}

export type ApplyCellTagsInput = (BaseInput & { mode: 'create' }) | (BaseInput & { mode: 'continue'; startPredecessorId: string });

export interface ApplyCellTagsResult {
  createdCount: number;
}

export const useApplyCellTags = () => {
  const dispatch = useDispatch();
  const { mutateAsync: batchCreateTags } = useBatchCreateTags();
  const { mutate: continueTag } = useContinueTag();
  const { mutateAsync: addTagsToCells } = useAddTagsToCells();
  const { references } = useEditorSelector((editor: any) => editor);
  const { clearTableTagSelection } = useContext(TagContext);

  return useCallback(
    async (input: ApplyCellTagsInput): Promise<ApplyCellTagsResult> => {
      const { cells, referenceDataSuperId, conceptId, type, taxonomyId, sectionContext, locale, onTagsCreated, blockedTagIds, origin, linkroleUri } = input;
      const referenceData = references?.find((r: { superId: string }) => r.superId === referenceDataSuperId);
      if (!referenceData) return { createdCount: 0 };

      const cellsToTag = cells.filter((cell) => {
        if (!blockedTagIds || blockedTagIds.size === 0) return true;
        const existingTagIds = referenceData.selectionContent?.[cell.row]?.[cell.col]?.tags ?? [];
        return !existingTagIds.some((id: string) => blockedTagIds.has(id));
      });
      if (cellsToTag.length === 0) return { createdCount: 0 };

      const createdTags: { tagId: string; row: number; col: number }[] = [];

      if (input.mode === 'create') {
        try {
          const bodies = cellsToTag.map((cell) => ({
            taxonomyId,
            documentSuperId: sectionContext.documentSuperId,
            sectionSuperId: sectionContext.sectionSuperId,
            pageId: sectionContext.pageId,
            onId: referenceDataSuperId,
            conceptId: cell.conceptId ?? conceptId,
            lang: locale,
            type,
            value: cell.value != null ? String(cell.value) : undefined,
            ...(origin ? { origin } : {}),
            ...((cell.linkroleUri ?? linkroleUri) != null ? { linkroleUri: cell.linkroleUri ?? linkroleUri } : {}),
          }));
          const created = await batchCreateTags(bodies);
          created.forEach((tag, i) => createdTags.push({ tagId: tag.id, row: cellsToTag[i].row, col: cellsToTag[i].col }));
        } catch (error) {}
      } else {
        let previousTagId = input.startPredecessorId;
        for (const cell of cellsToTag) {
          const predecessorForCell = previousTagId;
          try {
            const newTag = await new Promise<{ id: string }>((resolve, reject) => {
              continueTag(
                {
                  taxonomyId,
                  documentSuperId: sectionContext.documentSuperId,
                  sectionSuperId: sectionContext.sectionSuperId,
                  pageId: sectionContext.pageId,
                  onId: referenceDataSuperId,
                  conceptId,
                  lang: locale,
                  type,
                  value: cell.value != null ? String(cell.value) : undefined,
                  ...(linkroleUri != null ? { linkroleUri } : {}),
                  predecessorId: predecessorForCell,
                },
                {
                  onSuccess: (data: { id: string }) => resolve(data),
                  onError: (error: unknown) => reject(error),
                }
              );
            });
            createdTags.push({ tagId: newTag.id, row: cell.row, col: cell.col });
            previousTagId = newTag.id;
          } catch (error) {}
        }
      }

      if (createdTags.length === 0) return { createdCount: 0 };

      try {
        // @ts-ignore type error since query is in js file
        const result = await addTagsToCells({ referenceData, tagsToAdd: createdTags });
        const updatedReferenceData = result?.data;
        if (updatedReferenceData) {
          const updatedReferences = references.map((ref: { superId: string }) => (ref.superId === updatedReferenceData.superId ? updatedReferenceData : ref));
          dispatch(setReferenceData(updatedReferences));
        }
        onTagsCreated?.();
        clearTableTagSelection?.();
      } catch (error) {
        console.error('Error writing tag ids back to reference data:', error);
      }

      return { createdCount: createdTags.length };
    },
    [batchCreateTags, continueTag, addTagsToCells, references, dispatch, clearTableTagSelection]
  );
};
